import { zodResolver } from "@hookform/resolvers/zod"
import * as Sentry from "@sentry/react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { AxiosError } from "axios"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { CardDescription, CardTitle } from "design-system/molecules/Card"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import config from "lib/config"
import { getErrorTranslations } from "lib/i18n"
import { TRACKING_EVENTS } from "lopermut"
import mixpanel from "mixpanel-browser"
import { AuthWrapper } from "modules/auth/components/AuthWrapper"
import { useSignupByWebsiteMutation } from "modules/auth/hooks/useSignupByWebsiteMutation"
import { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { Errors, SignupByWebsiteSchema } from "schema"
import { z } from "zod"

export const Route = createFileRoute("/_base/_public/signup-website/")({
  component: SignupWebsite,
})

type FieldValues = z.infer<typeof SignupByWebsiteSchema>

function SignupWebsite() {
  const { t, i18n } = useTranslation()
  const { mutateAsync, isPending } = useSignupByWebsiteMutation()
  const [isRequested, setIsRequested] = useState(false)

  const locale = i18n.language === "es-ES" ? "es_ES" : "en_US"

  const form = useForm<FieldValues>({
    resolver: zodResolver(SignupByWebsiteSchema),
    defaultValues: { email: "", phone: "", locale },
  })

  const onSubmit: SubmitHandler<FieldValues> = async ({ email, phone, locale: detectedLocale }) => {
    try {
      await mutateAsync({ email, phone, locale: detectedLocale })
      setIsRequested(true)
    } catch (err) {
      if (!(err instanceof AxiosError && err.response?.status === 422)) {
        Sentry.captureException(err)
      }
      const errorTranslations = getErrorTranslations()
      const key: Errors =
        err instanceof AxiosError
          ? (err.response?.data?.error?.key ?? Errors.GENERIC_ERROR)
          : Errors.GENERIC_ERROR
      form.setError("email", { message: errorTranslations[key] })
    }
  }

  return (
    <AuthWrapper>
      <div className="flex flex-col gap-4 w-full justify-center items-center">
        {isRequested ? (
          <div className="flex flex-col gap-2 items-center text-center">
            <CardTitle>{t("auth.signup.success.title")}</CardTitle>
            <CardDescription>{t("auth.signup.success.body")}</CardDescription>
          </div>
        ) : (
          <>
            <Form
              {...form}
              errorTranslations={getErrorTranslations()}
              onSubmit={(e) => {
                if (config.isProduction) {
                  mixpanel.track(TRACKING_EVENTS.SIGNUP_WEBSITE_SUBMIT, {
                    email: form.getValues("email"),
                  })
                }
                return form.handleSubmit(onSubmit)(e)
              }}
              className="w-full"
            >
              <div className="flex flex-col gap-4 mb-4 items-center text-xl">
                <CardTitle>{t("auth.signupWebsite.title")}</CardTitle>
                <CardDescription className="text-foreground text-center">
                  {t("auth.signupWebsite.description")}
                </CardDescription>
              </div>
              <div className="gap-4 flex flex-col w-full">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.corporateEmail.field")}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t("auth.corporateEmail.placeholder")}
                          autoComplete="email"
                          className="bg-background/75"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.signupWebsite.phone.field")}</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder={t("auth.signupWebsite.phone.placeholder")}
                          autoComplete="tel"
                          className="bg-background/75"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-4 pt-4">
                <Button disabled={isPending}>{t("auth.signupWebsite.submit")}</Button>
              </div>
            </Form>
            <div className="text-center text-sm">
              <Trans
                i18nKey={"auth.signupWebsite.goToLogin"}
                components={{
                  b: <Link className="underline inline hover:text-foreground/80" to="/login" />,
                }}
              />
            </div>
          </>
        )}
      </div>
    </AuthWrapper>
  )
}
